import type { DocsService } from '@toeverything/infra';
import { Entity, LiveData } from '@toeverything/infra';

import { Tag } from '../entities/tag';
import type { TagStore } from '../stores/tag';

export class TagList extends Entity {
  constructor(
    private readonly store: TagStore,
    private readonly docs: DocsService
  ) {
    super();
  }

  private readonly pool = new Map<string, Tag>();

  readonly tags$ = LiveData.from(this.store.watchTagIds(), []).map((ids: string[]) => {
    return ids.map((id: string) => {
      const exists = this.pool.get(id);
      if (exists) {
        return exists;
      }
      const record = this.framework.createEntity(Tag, { id });
      this.pool.set(id, record);
      return record;
    });
  });

  createTag(value: string, color: string) {
    const newId = this.store.createNewTag(value, color);
    const newTag = this.framework.createEntity(Tag, { id: newId });
    return newTag;
  }

  deleteTag(tagId: string) {
    this.store.deleteTag(tagId);
  }

  tagsByPageId$(pageId: string) {
    return LiveData.computed((get: <T>(data: LiveData<T>) => T) => {
      const docRecord = get(this.docs.list.doc$(pageId));
      if (!docRecord) return [];
      const tagIds = (get((docRecord as { meta$: LiveData<{ tags?: string[] }> }).meta$) as { tags?: string[] }).tags;

      return (get(this.tags$) as Tag[]).filter((tag: Tag) => (tagIds ?? []).includes(tag.id));
    });
  }

  tagIdsByPageId$(pageId: string) {
    return this.tagsByPageId$(pageId).map((tags: Tag[]) => tags.map((tag: Tag) => tag.id));
  }

  tagByTagId$(tagId?: string) {
    return this.tags$.map((tags: Tag[]) => tags.find((tag: Tag) => tag.id === tagId));
  }

  tagMetas$ = LiveData.computed((get: <T>(data: LiveData<T>) => T) => {
    return (get(this.tags$) as Tag[]).map((tag: Tag) => {
      return {
        id: tag.id,
        title: get(tag.value$) as string,
        color: get(tag.color$) as string,
        pageCount: (get(tag.pageIds$) as string[]).length,
        createDate: get(tag.createDate$) as number,
        updatedDate: get(tag.updateDate$) as number,
      };
    });
  });

  private filterFn(value: string, query?: string) {
    const trimmedQuery = query?.trim().toLowerCase() ?? '';
    const trimmedValue = value.trim().toLowerCase();
    return trimmedValue.includes(trimmedQuery);
  }

  filterTagsByName$(name: string) {
    return LiveData.computed((get: <T>(data: LiveData<T>) => T) => {
      return (get(this.tags$) as Tag[]).filter((tag: Tag) =>
        this.filterFn(get(tag.value$) as string, name)
      );
    });
  }
}
